/*!
 * Copyright 2022 Thorne Health Lab. All rights reserved.
 */
import React, { Component, Fragment } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import injectSheet from 'react-jss'
import { RadioGroup, Radio } from 'react-radio-group'
import Select from 'react-select'
import Cleave from 'cleave.js/react'

import moment from 'moment-timezone'

import Button from '../../components/Button'
import Spinner from '../../components/Spinner'
import TextInput from '../../components/TextInput'

import { titleCase } from '../../utils'
import { USER_URL, VISIT_URL } from '../../api/endpoints'
import { constants as L100Constants } from '../../constants'
import * as actionCreators from '../../root/actions'

import style from './style'

const eduOptions = [
  { value: L100Constants.highSchool, label: 'High School' },
  {
    value: L100Constants.someCollege,
    label: 'Some college but less than 1 year'
  },
  {
    value: L100Constants.incomplete2YearDegree,
    label: 'One or more years of college at a 2-year program, no degree'
  },
  {
    value: L100Constants.oneYearCollege,
    label: 'One year of college at a 4-year program, no degree'
  },
  {
    value: L100Constants.twoYearCollege,
    label: 'Two years of college at a 4-year program, no degree'
  },
  {
    value: L100Constants.threeYearCollege,
    label: 'Three years or more of college at a 4-year program, no degree'
  },
  { value: L100Constants.associatesDegree, label: 'Associates Degree' },
  { value: L100Constants.bachelorsDegree, label: 'Bachelors Degree' },
  { value: L100Constants.mastersDegree, label: 'Masters Degree' },
  {
    value: L100Constants.professionalDegree,
    label: 'Professional degree (e.g., MD, DDS, DVM)'
  },
  { value: L100Constants.doctorateDegree, label: 'Doctorate' }
]

export class Profile extends Component {
  state = {
    type: '',
    firstName: '',
    lastName: '',
    email: '',
    dateOfBirth: '',
    gender: '',
    race: '',
    ethnicity: '',
    education: '',
    dexterity: '',
    hasPacemaker: '',
    updatedProfile: false,
    isSubmitting: false
  }

  render() {
    const { api, classes } = this.props

    return (
      <div className={classes.base}>
        {api.get('isLoading') ? (
          <Spinner />
        ) : (
          <Fragment>
            {this.state.updatedProfile ? (
              <div className="loaderHolder" style={{ fontWeight: 500 }}>
                Profile Updated!
              </div>
            ) : (
              <form onSubmit={this.onSubmit}>{this.renderView()}</form>
            )}
          </Fragment>
        )}
      </div>
    )
  }

  componentDidMount() {
    const { getUserFromDB } = this.props
    const meta = {
      onSuccess: result => {
        const { app } = this.props.containers

        if (app.get('isMobile')) {
          this.props.highlightNavbarItem('updateProfile')
        }

        const user = result.data.data
        const { first_name, last_name, email, demographics } = user // eslint-disable-line
        const {
          hasPacemaker,
          gender,
          dexterity,
          race,
          ethnicity,
          education,
          birth_date // eslint-disable-line
        } = demographics

        const data = {
          firstName: titleCase(first_name),
          lastName: titleCase(last_name),
          email,
          hasPacemaker,
          ethnicity,
          gender,
          type: user.type,
          dexterity,
          education,
          race,
          dateOfBirth: moment(birth_date)
            .utc()
            .format('MM/DD/YYYY')
        }

        this.setState(data)
      }
    }
    getUserFromDB(meta)
  }

  renderView() {
    const { signup, app } = this.props.containers
    const {
      firstName,
      lastName,
      email,
      dateOfBirth,
      gender,
      race,
      ethnicity,
      education,
      dexterity,
      hasPacemaker
    } = this.state

    return (
      <div className="mobile-padding">
        <h2>Update Your Personal Information</h2>

        <TextInput
          type="text"
          placeholder="First Name"
          value={firstName}
          required
          className="underlineText marginUnder"
          onChange={({ target }) => this.setState({ firstName: target.value })}
        />

        <TextInput
          type="text"
          placeholder="Last Name"
          value={lastName}
          required
          className="underlineText marginUnder"
          onChange={({ target }) => this.setState({ lastName: target.value })}
        />

        <TextInput
          type="text"
          placeholder="Email"
          value={email}
          required
          className="underlineText marginUnder"
          onChange={({ target }) => this.setState({ email: target.value })}
        />

        <br />
        <br />
        {!app.get('isMobile') && <br />}
        <div className="inputTitle">Date of Birth (MM/DD/YYYY)</div>

        <Cleave
          options={{
            date: true,
            datePattern: ['m', 'd', 'Y']
          }}
          value={dateOfBirth}
          required
          className="datePicker underlineText marginUnder"
          onChange={({ target }) =>
            this.setState({ dateOfBirth: target?.value || '' })
          }
          placeholder="MM/DD/YYYY"
        />
        <br />
        <br />
        {!app.get('isMobile') && <br />}

        <div className="inputTitle">Gender</div>

        <div className="inlineRadioContainer">
          <RadioGroup
            name="gender"
            selectedValue={gender}
            onChange={value => this.setState({ gender: value })}
          >
            <Radio id="gender2" className="radio" value={L100Constants.male} />
            <label htmlFor="gender2">Male</label>
            <Radio id="gender1" value={L100Constants.female} required />
            <label htmlFor="gender1">Female</label>
          </RadioGroup>
        </div>
        <br />
        {!app.get('isMobile') && <br />}

        <div className="inputTitle">Race (Please select your primary race)</div>
        <div className="blockRadioContainer">
          <RadioGroup
            name="race"
            selectedValue={race}
            onChange={value => this.setState({ race: value })}
          >
            <Radio id="race1" value={L100Constants.americanIndian} required />
            <label htmlFor="race1">American Indian or Alaska Native</label>
            <Radio id="race2" value={L100Constants.asian} />
            <label htmlFor="race2">Asian</label>
            <Radio id="race3" value={L100Constants.africanAmerican} />
            <label htmlFor="race3">Black or African American</label>
            <Radio id="race4" value={L100Constants.pacificIslander} />
            <label htmlFor="race4">
              Native Hawaiian or Other Pacific Islanders
            </label>
            <Radio id="race5" value={L100Constants.white} />
            <label htmlFor="race5">White</label>
            <Radio id="race6" value={L100Constants.other} />
            <label htmlFor="race6">Other</label>
          </RadioGroup>
        </div>
        <br />
        {!app.get('isMobile') && <br />}

        <div className="inputTitle">
          Ethnicity (Please select your primary ethnicity)
        </div>
        <div className="blockRadioContainer">
          <RadioGroup
            name="ethnicity"
            selectedValue={ethnicity}
            onChange={value => this.setState({ ethnicity: value })}
          >
            <div className="">
              <Radio id="ethnicity1" value={L100Constants.hispanic} required />
              <label htmlFor="ethnicity1">Hispanic or Latino</label>
            </div>
            <div>
              <Radio id="ethnicity2" value={L100Constants.nonHispanic} />
              <label htmlFor="ethnicity2">Non-Hispanic or Latino</label>
            </div>
          </RadioGroup>
        </div>
        <br />
        {!app.get('isMobile') && <br />}

        <div className="inputTitle">
          What is the highest level of your education?
        </div>
        <div className="profileSelect">
          <Select
            options={eduOptions}
            onChange={({ value }) => this.setState({ education: value })}
            value={education}
            placeholder="Select an option"
            clearable={false}
            searchable={false}
            required
          />
        </div>
        {!app.get('isMobile') && <br />}

        <div className="inputTitle">Left or Right Handed</div>
        <div className="inlineRadioContainer">
          <RadioGroup
            name="dexterity"
            selectedValue={dexterity}
            onChange={value => this.setState({ dexterity: value })}
          >
            <Radio id="hand1" value={L100Constants.left} required />
            <label htmlFor="hand1">Left</label>
            <Radio id="hand2" value={L100Constants.right} />
            <label htmlFor="hand2">Right</label>
          </RadioGroup>
        </div>

        <br />
        {!app.get('isMobile') && <br />}

        <div className="inputTitle">Do you have a Pacemaker?</div>
        <div className="inlineRadioContainer">
          <RadioGroup
            name="hasPacemaker"
            selectedValue={hasPacemaker}
            onChange={value => this.setState({ hasPacemaker: value })}
          >
            <Radio id="hasPacemaker1" value={L100Constants.no} required />
            <label htmlFor="hasPacemaker1">No</label>
            <Radio id="hasPacemaker2" value={L100Constants.yes} />
            <label htmlFor="hasPacemaker2">Yes</label>
          </RadioGroup>
        </div>
        <br />
        {!PRODUCTION && (
          <Fragment>
            {!app.get('isMobile') && <br />}

            <div className="inputTitle">Type of User</div>
            <div className="blockRadioContainer">
              <RadioGroup
                name="user-type"
                selectedValue={this.state.type}
                onChange={value => this.setState({ type: value })}
              >
                <Radio
                  id="user-type0"
                  value={L100Constants.superVip}
                  required
                />
                <label htmlFor="user-type0">Admin</label>
                <Radio id="user-type2" value={L100Constants.vip} />
                <label htmlFor="user-type2">VIP</label>
                <Radio id="user-type3" value={L100Constants.partner} />
                <label htmlFor="user-type3">Partner</label>
                <Radio id="user-type4" value={L100Constants.regular} />
                <label htmlFor="user-type4">Regular</label>
              </RadioGroup>
            </div>
          </Fragment>
        )}
        <div>
          {signup.get('isLoading') ? (
            <Spinner />
          ) : (
            <Fragment>
              <br />
              <br />
              <br />
              <Button
                className="fullWidthButton update-profile-btn"
                disabled={!!signup.get('isLoading')}
                type="submit"
              >
                Update Information
              </Button>
            </Fragment>
          )}
        </div>

        {!PRODUCTION && (
          <Fragment>
            <br />
            <br />
            <br />
            <br />
            {!app.get('isMobile') && <br />}

            <hr />

            {!app.get('isMobile') && (
              <Fragment>
                <br />
              </Fragment>
            )}

            <h2 className={app.get('isMobile') ? '' : 'noMarginPadding'}>
              Delete Your Account
            </h2>

            <div className="footnote">
              Taking this action will delete your account. This action is not
              reversible.
            </div>
            <br />
            <div>
              <Button
                className="fullWidthButton"
                type="button"
                scheme="light"
                warning
                onClick={this.onDeleteAccount}
              >
                Delete Account
              </Button>
            </div>
            <br />
            <br />
            {!app.get('isMobile') && <br />}
            {!app.get('isMobile') && <br />}
            <h2 className={app.get('isMobile') ? '' : 'noMarginPadding'}>
              Complete a Visit with Demo Data
            </h2>

            <div className="footnote">
              Taking this action will complete a scheduled visit and create a
              report with demo data.
            </div>
            <br />
            <div>
              <Button
                className="fullWidthButton complete-visit-btn"
                type="button"
                scheme="light"
                warning
                onClick={this.onCreateFakeVisit}
                disabled={app.get('upcomingVisit') === null}
              >
                Complete Visit
              </Button>
            </div>
          </Fragment>
        )}
      </div>
    )
  }

  onDeleteAccount = () => {
    const { apiRequest, logout } = this.props

    const request = {
      url: USER_URL,
      method: 'delete'
    }

    const meta = {
      onSuccess: logout,
      Component: 'Profile'
    }

    apiRequest(request, meta)
  }

  onCreateFakeVisit = () => {
    const { apiRequest, logout, containers } = this.props
    const { app } = containers

    if (app.get('upcomingVisit') && app.get('upcomingVisit').uuid) {
      const request = {
        url: `${VISIT_URL}/${app.get('upcomingVisit').uuid}/demo`,
        method: 'put'
      }

      const meta = {
        onSuccess: logout,
        Component: 'Profile'
      }

      apiRequest(request, meta)
    }
  }

  onSubmit = e => {
    e.preventDefault()
    if (this.state.isSubmitting) return
    this.setState({ isSubmitting: true })
    const { apiRequest } = this.props
    const {
      firstName,
      lastName,
      email,
      dateOfBirth,
      gender,
      race,
      ethnicity,
      education,
      dexterity,
      hasPacemaker
    } = this.state

    const data = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
      type: this.state.type,
      demographics: {
        hasPacemaker: hasPacemaker,
        ethnicity,
        gender,
        dexterity,
        education,
        race,
        birth_date: moment(dateOfBirth)
          .utc()
          .format()
      }
    }

    const request = {
      url: USER_URL,
      data,
      method: 'put'
    }

    const meta = {
      ignoreAuth: true,
      onError: () => this.setState({ isSubmitting: false }),
      onSuccess: () => {
        this.setState({ updatedProfile: true }, () => {
          this.props.getUserFromDBAndUpdateStore()
          if (this.props.onSubmit) {
            setTimeout(() => {
              this.setState({ isSubmitting: false }, () =>
                this.props.onSubmit()
              )
            }, 1000)
          } else {
            setTimeout(() => {
              this.setState({ isSubmitting: false }, () =>
                this.props.changeRoute({ route: '/' })
              )
            }, 1000)
          }
        })
      },
      Component: 'Profile'
    }

    apiRequest(request, meta)
  }
}

const mapStateToProps = state => {
  const { containers, api } = state

  return {
    containers,
    api
  }
}

const mapDispatchToProps = dispatch => {
  return bindActionCreators(actionCreators, dispatch)
}

export default injectSheet(() => style)(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(Profile)
)
